import React, { useState, useEffect } from "react";
import { sendEmailComission } from "./emailComission";
import "./userinfodetails.css"; // Import the new CSS file

const UserInfoDetails2 = ({ selectedUser }) => {
    const [orders, setOrders] = useState([]);
    const [editedOrders, setEditedOrders] = useState([]);
    const [newOrderId, setNewOrderId] = useState(""); // For the new order document name
    const [newOrderTotalAmount, setNewOrderTotalAmount] = useState(""); // For the total amount
    const [newOrderCommissionPercentage, setNewOrderCommissionPercentage] = useState("36"); // Default to 36%
    const [successMessage, setSuccessMessage] = useState("");

    useEffect(() => {
        if (selectedUser) {
            const fetchOrders = async () => {
                try {
                    const response = await fetch(
                        `https://bymolive-backend-995926394015.us-central1.run.app/api/all-collections`
                    );
                    if (!response.ok) throw new Error("Failed to fetch user collections.");
                    const data = await response.json();

                    // Get all documents except the `00info` document
                    const userDocs = data[selectedUser.email];
                    if (!userDocs) {
                        throw new Error(`No documents found for ${selectedUser.email}`);
                    }

                    const orders = userDocs.filter((doc) => doc.id !== "00info" && doc.id !== "00Notifications");
                    setOrders(orders);
                    setEditedOrders(orders);
                } catch (error) {
                    console.error("Error fetching orders:", error);
                }
            };

            fetchOrders();
        }
    }, [selectedUser]);

    const handleFieldChange = (orderId, field, value) => {
        setEditedOrders((prevOrders) =>
            prevOrders.map((order) =>
                order.id === orderId
                    ? { ...order, [field]: field === "amount" || field === "comision" ? parseFloat(value) || 0 : value }
                    : order
            )
        );
    };

    const handleAddOrder = () => {
        if (!newOrderId.trim() || !newOrderTotalAmount.trim()) {
            setSuccessMessage("Please provide a valid document name and total amount.");
            return;
        }

        const totalAmount = parseFloat(newOrderTotalAmount);
        const amount = totalAmount / 1.19;
        const commissionPercentage = parseFloat(newOrderCommissionPercentage) || 36;
        const comision = amount * (commissionPercentage / 100) * 0.9;

        // Get today's date in the required format (DD.MM.YYYY)
        const today = new Date();
        const formattedDate = `${String(today.getDate()).padStart(2, "0")}.${String(today.getMonth() + 1).padStart(
            2,
            "0"
        )}.${today.getFullYear()}`;

        const newOrder = {
            id: newOrderId.trim(), // Document name entered by the user
            date: formattedDate, // Default to today's date
            amount: parseFloat(amount.toFixed(2)), // Calculated amount
            comision: parseFloat(comision.toFixed(2)), // Calculated comision
            status: "in asteptare", // Default status
        };

        setEditedOrders((prevOrders) => [...prevOrders, newOrder]);
        setOrders((prevOrders) => [...prevOrders, newOrder]);
        setNewOrderId(""); // Clear the input fields
        setNewOrderTotalAmount("");
        setNewOrderCommissionPercentage("36"); // Reset commission percentage to default
        setSuccessMessage("New order added successfully.");
    };

    const handleDeleteOrder = async (orderId) => {
        try {
            // Make DELETE request to API
            const response = await fetch(
                "https://bymolive-backend-995926394015.us-central1.run.app/api/delete-document",
                {
                    method: "DELETE",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        collection: selectedUser.email,
                        id: orderId,
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to delete order with ID: ${orderId}`);
            }

            // Remove the order from state
            setEditedOrders((prevOrders) => prevOrders.filter((order) => order.id !== orderId));
            setOrders((prevOrders) => prevOrders.filter((order) => order.id !== orderId));
            setSuccessMessage(`Order ${orderId} deleted successfully.`);
        } catch (error) {
            console.error("Error deleting order:", error);
            setSuccessMessage("Error deleting order. Please try again.");
        }
    };

    const handleSaveOrders = async () => {
        try {
            for (const order of editedOrders) {
                const response = await fetch(
                    "https://bymolive-backend-995926394015.us-central1.run.app/api/update-collection",
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            collection: selectedUser.email,
                            document: order,
                        }),
                    }
                );
                if (!response.ok) {
                    throw new Error(`Failed to update order with ID: ${order.id}`);
                }
            }
            setSuccessMessage("Orders saved successfully!");
        } catch (error) {
            console.error("Error saving orders:", error);
            setSuccessMessage("Error saving orders. Please try again.");
        }
    };

    const handleSendEmail = async (order) => {
        try {
            await sendEmailComission({
                recipientEmail: selectedUser.email,
                orderNumber: order.id,
                comisionAmount: order.comision,
            });
            setSuccessMessage(`Email sent for Order ${order.id}`);
        } catch (error) {
            setSuccessMessage(`Failed to send email for Order ${order.id}`);
        }
    };

    return (
        <div className="user-orders-container">
            <h3>Orders for {selectedUser.email}</h3>
            <table className="orders-table">
                <thead>
                    <tr>
                        <th>Notification</th>
                        <th>ID</th>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Comision</th>
                        <th>Status</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {editedOrders.map((order) => (
                        <tr key={order.id}>
                            <td>
                                {order.notification_sent_at ? (
                                    <span className="notification-sent">Sent</span>
                                ) : (
                                    <button 
                                        className="email-button"
                                        onClick={() => handleSendEmail(order)}
                                    >
                                        Send email
                                    </button>
                                )}
                            </td>
                            <td>{order.id}</td>
                            <td>
                                <input
                                    type="text"
                                    value={order.date}
                                    onChange={(e) =>
                                        handleFieldChange(order.id, "date", e.target.value)
                                    }
                                />
                            </td>
                            <td>
                                <input
                                    type="number"
                                    value={order.amount}
                                    onChange={(e) =>
                                        handleFieldChange(order.id, "amount", e.target.value)
                                    }
                                />
                            </td>
                            <td>
                                <input
                                    type="number"
                                    value={order.comision}
                                    onChange={(e) =>
                                        handleFieldChange(order.id, "comision", e.target.value)
                                    }
                                />
                            </td>
                            <td>
                                <select
                                    value={order.status}
                                    onChange={(e) => handleFieldChange(order.id, "status", e.target.value)}
                                >
                                    <option value="in asteptare">in asteptare</option>
                                    <option value="user">user</option>
                                    <option value="aprobat">aprobat</option>
                                    <option value="retur">retur</option>
                                </select>
                            </td>
                            <td>
                                <button 
                                    className="delete-order-button"
                                    onClick={() => handleDeleteOrder(order.id)}
                                >
                                    Delete
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <div className="add-order-section">
                <h4>ADD NEW ORDER</h4>
                <div className="input-container">
                    <input
                        type="text"
                        placeholder="Order #"
                        value={newOrderId}
                        onChange={(e) => setNewOrderId(e.target.value)}
                    />
                    <input
                        type="number"
                        placeholder="Total incl Taxes"
                        value={newOrderTotalAmount}
                        onChange={(e) => setNewOrderTotalAmount(e.target.value)}
                    />
                    <div className="commission-input-wrapper">
                        <input
                            type="number"
                            placeholder="Commission %"
                            value={newOrderCommissionPercentage}
                            onChange={(e) => setNewOrderCommissionPercentage(e.target.value)}
                            className="commission-input"
                        />
                        <span className="percentage-symbol">%</span>
                    </div>
                    <div className="orders-add-order-button">
                        <button onClick={handleAddOrder}>Add Order →</button>
                    </div>
                </div>
            </div>

            <div className="add-order-section-button">
                <button onClick={handleSaveOrders}>Save Orders</button>
                {successMessage && <p>{successMessage}</p>}
            </div>
        </div>
    );
};

export default UserInfoDetails2;
